// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import PageProps from "@insite/client-framework/Types/PageProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import { addressProps } from "@store/Themes/common/props/address";
import { formGridProps } from "@store/Themes/common/props/form";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Loader from "@store/Library/Components/Toolkit/Loader";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import { AddressInfoDisplayStyles } from "@store/Overrides/Components/AddressInfoDisplay";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";

type Props = WidgetProps & PageProps;

export interface AccountSettingsAddressesStyles {
    customerSelectorGridContainer?: GridContainerProps;
    useDefaultCustomerCheckboxGroup?: CheckboxGroupProps;
    useDefaultCustomerCheckbox?: CheckboxPresentationProps;
    container?: InjectableCss;
    addresses?: InjectableCss;
    headingText?: TypographyPresentationProps;
    checkboxContainer?: InjectableCss;
    separator?: InjectableCss;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    address?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const accountSettingsAddressesStyles: AccountSettingsAddressesStyles = {
    title: formGridProps.title,
    container: {
        css: css`
            border: ${pxToRem(1)} solid ${getColor("common.border")};
            padding: ${pxToRem(10)} ${pxToRem(20)};
            border-radius: ${pxToRem(8)};
        `,
    },
    addresses: {
        css: css`
            display: flex;
            margin-top: ${pxToRem(20)};
            gap: ${pxToRem(20)};
        `,
    },
    address: {
        css: css`
            flex: 1;
        `,
    },
    headingText: {
        css: css`
            ${addressProps.title?.css}
            margin-bottom: 0;
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    checkboxContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(5)};
        `,
    },
    separator: {
        css: css`
            border-right: ${pxToRem(1)} solid ${getColor("common.border")};
            padding: ${pxToRem(10)} 0;
            margin: 0 ${pxToRem(5)};
        `,
    },
};

const styles = accountSettingsAddressesStyles;

const AccountSettingsAddresses: React.FC<Props> = () => {
    const dispatch = useAppDispatch();
    const { shipToId } = useAppSelector(state => getSession(state));
    const currentBillTo = useAppSelector(state => getCurrentBillToState(state).value);
    const currentShipTo = useAppSelector(state => getCurrentShipToState(state).value);
    const currentShipTosDataView = useAppSelector(state => getCurrentShipTosDataView(state));
    const { selectedShipToId } = useAppSelector(state => state.pages.accountSettings);
    const isDefaultCustomer = selectedShipToId === shipToId;
    const [defaultCustomer, setDefaultCustomer] = useState(isDefaultCustomer);

    useEffect(() => {
        if (defaultCustomer !== isDefaultCustomer) {
            setDefaultCustomer(isDefaultCustomer);
        }
    }, [selectedShipToId, shipToId]);

    const defaultShippingChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setDefaultCustomer(value);
        dispatch(updateAccountSettings(value ? { shipToId } : { useDefaultCustomer: false }));
    };

    return (
        <>
            <Typography {...styles.title}>{translate("Addresses")}</Typography>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.checkboxContainer}>
                    <Typography {...styles.headingText}>{translate("Active Account")}</Typography>
                    <StyledWrapper {...styles.separator} as="span" aria-hidden="true" />
                    <CheckboxGroup {...styles.useDefaultCustomerCheckboxGroup}>
                        <Checkbox
                            {...styles.useDefaultCustomerCheckbox}
                            aria-describedby="setDefaultCustomer-description"
                            checked={defaultCustomer}
                            data-test-selector="changeCustomer_useDefaultCustomer"
                            onChange={defaultShippingChangeHandler}
                        >
                            {siteMessage("DefaultCustomer_SetToUser")}
                        </Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
                <StyledWrapper {...styles.addresses}>
                    <StyledWrapper {...styles.address} data-test-selector="addressInfoDisplay_CurrentBillingAddress">
                        <Typography {...styles.headingText}>{translate("Billing Information")}</Typography>
                        {!currentBillTo ? (
                            <Loader />
                        ) : (
                            <AddressInfoDisplay
                                firstName={currentBillTo.firstName}
                                lastName={currentBillTo.lastName}
                                companyName={currentBillTo.companyName}
                                attention={currentBillTo.attention}
                                address1={currentBillTo.address1}
                                address2={currentBillTo.address2}
                                address3={currentBillTo.address3}
                                address4={currentBillTo.address4}
                                city={currentBillTo.city}
                                state={currentBillTo.state ? currentBillTo.state.abbreviation : undefined}
                                postalCode={currentBillTo.postalCode}
                                country={currentBillTo.country ? currentBillTo.country.abbreviation : undefined}
                                phone={currentBillTo.phone}
                                fax={currentBillTo.fax}
                                email={currentBillTo.email}
                                extendedStyles={styles.addressInfoDisplay}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.address} data-test-selector="addressInfoDisplay_CurrentShippingAddress">
                        <Typography {...styles.headingText}>{translate("Shipping Information")}</Typography>
                        {currentShipTosDataView.isLoading || !currentShipTo || !currentBillTo ? (
                            <Loader />
                        ) : (
                            <AddressInfoDisplay
                                firstName={currentShipTo.firstName}
                                lastName={currentShipTo.lastName}
                                companyName={currentShipTo.companyName}
                                attention={currentShipTo.attention}
                                address1={currentShipTo.address1}
                                address2={currentShipTo.address2}
                                address3={currentShipTo.address3}
                                address4={currentShipTo.address4}
                                city={currentShipTo.city}
                                state={currentShipTo.state ? currentShipTo.state.abbreviation : undefined}
                                postalCode={currentShipTo.postalCode}
                                country={currentShipTo.country ? currentShipTo.country.abbreviation : undefined}
                                phone={currentShipTo.phone}
                                fax={currentShipTo.fax}
                                email={currentShipTo.email}
                                extendedStyles={styles.addressInfoDisplay}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: AccountSettingsAddresses,
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "Account Settings Addresses",
    },
};

export default widgetModule;
